import React, { useState } from 'react';
import { Plus, Radio, Globe, Users, Video, Mic, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArenaType } from './ArenaTypeBadge';
import { ArenaTagSelector } from './ArenaTagSelector';
import { toast } from 'sonner';

interface CreateArenaModalProps {
  onCreateArena: (arenaData: CreateArenaData) => Promise<boolean>;
}

export interface CreateArenaData {
  name: string;
  topic: string;
  description: string;
  arena_type: ArenaType;
  language: string;
  max_participants: number;
  is_video_enabled: boolean;
  is_voice_only: boolean;
  tags?: string[];
}

export interface CreateArenaData {
  name: string;
  topic: string;
  description: string;
  arena_type: ArenaType;
  language: string;
  max_participants: number;
  is_video_enabled: boolean;
  is_voice_only: boolean;
}

const TOPICS = [
  'Flirting',
  'Relationships',
  'Travel',
  'Late Night',
  'Casual',
  'Adventure',
  'Food & Dining',
  'Music',
  'Movies',
  'Sports',
  'Gaming',
  'Art & Culture',
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
];

const ARENA_TYPES: { value: ArenaType; label: string; description: string }[] = [
  { value: 'vibe_booth', label: '💬 Vibe Booth', description: 'Casual hangout' },
  { value: 'dating_arena', label: '💘 Dating Arena', description: 'Find your match' },
  { value: 'global_arena', label: '🌍 Global Arena', description: 'Meet worldwide' },
  { value: 'after_hours', label: '🕯️ After Hours', description: 'Late night vibes' },
  { value: 'event_arena', label: '🎉 Event Arena', description: 'Special events' },
];

export const CreateArenaModal: React.FC<CreateArenaModalProps> = ({ onCreateArena }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [formData, setFormData] = useState<Omit<CreateArenaData, 'tags'>>({
    name: '',
    topic: 'Casual',
    description: '',
    arena_type: 'vibe_booth',
    language: 'en',
    max_participants: 50,
    is_video_enabled: true,
    is_voice_only: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter an arena name');
      return;
    }

    setLoading(true);
    try {
      const success = await onCreateArena({
        ...formData,
        tags: selectedTags,
      });
      if (success) {
        toast.success('Arena created! 🎉');
        setOpen(false);
        setFormData({
          name: '',
          topic: 'Casual',
          description: '',
          arena_type: 'vibe_booth',
          language: 'en',
          max_participants: 50,
          is_video_enabled: true,
          is_voice_only: false,
        });
        setSelectedTags([]);
      }
    } catch (error) {
      toast.error('Failed to create arena');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-gradient-to-r from-primary to-secondary text-white shadow-lg"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-1" />
          Create Arena
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-primary" />
            Create Live Arena
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Arena Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Arena Name *</Label>
            <Input
              id="name"
              placeholder="Give your arena a catchy name..."
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              maxLength={50}
            />
          </div>

          {/* Topic */}
          <div className="space-y-2">
            <Label>Topic</Label>
            <Select
              value={formData.topic}
              onValueChange={(value) => setFormData({ ...formData, topic: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOPICS.map((topic) => (
                  <SelectItem key={topic} value={topic}>
                    {topic}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Arena Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Arena Type
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {ARENA_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, arena_type: type.value })}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    formData.arena_type === type.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="What's this arena about?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              maxLength={200}
            />
          </div>

          {/* Topic Tags */}
          <ArenaTagSelector
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            maxTags={5}
          />
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Language
            </Label>
            <Select
              value={formData.language}
              onValueChange={(value) => setFormData({ ...formData, language: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Max Participants */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Max Participants
            </Label>
            <Select
              value={formData.max_participants.toString()}
              onValueChange={(value) =>
                setFormData({ ...formData, max_participants: parseInt(value) })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 people</SelectItem>
                <SelectItem value="25">25 people</SelectItem>
                <SelectItem value="50">50 people</SelectItem>
                <SelectItem value="100">100 people</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Media Settings */}
          <div className="space-y-4 bg-muted/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="video" className="flex items-center gap-2 cursor-pointer">
                <Video className="w-4 h-4" />
                Enable Video
              </Label>
              <Switch
                id="video"
                checked={formData.is_video_enabled}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_video_enabled: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="voice-only" className="flex items-center gap-2 cursor-pointer">
                <Mic className="w-4 h-4" />
                Voice Only Mode
              </Label>
              <Switch
                id="voice-only"
                checked={formData.is_voice_only}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_voice_only: checked, is_video_enabled: !checked })
                }
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full rounded-full bg-gradient-to-r from-primary to-secondary"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </div>
            ) : (
              <>
                <Radio className="w-4 h-4 mr-2" />
                Go Live!
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateArenaModal;
